import { Action } from '@ngrx/store';
import * as fromLookups from '../../lookups';
import { IDirectReport } from '../../lookups';
import { IAttachment, IGenericResponse } from '../../shared';
import { ExpenseRequestTypeEnum } from '../expense-enums';
import {
    IExpenseDetailResponse,
    IExpenseLine,
    IExpenseLineResponse
} from '../IExpenseLine';
import { IExpenseReceipt, IExpenseReceiptResponse } from '../IExpenseReceipt';
import {
    IDeleteReceiptResponse,
    IExpenseSummary,
    IExpenseSummaryResponse
} from '../IExpenseSummary';
import { IExpenseTask } from '../IExpenseTask';
import { IPaymentMethod } from '../IPaymentMethod';
import { ITravelMode } from '../ITravelMode';
import { ITravelRate } from '../ITravelRate';
import { ITripLegType } from '../ITripLegType';
import { UserPaymentMethod } from '../UserPaymentMethod';

export enum ActionTypes {
    Reset = '[expenses] Reset',
    ResetSelectedExpense = '[expenses] Reset Selected Expense',
    ClearMessages = '[expenses] Clear Messages',
    AddExpenseSummary = '[expenses] Add Expense Summary',
    AddExpenseSummarySuccess = '[expenses] Add Expense Summary Success',
    AddExpenseSummaryFailure = '[expenses] Add Expense Summary Failure',
    AddExpenseDetails = '[expenses] Add Expense Details',
    AddExpenseDetailsFailure = '[expenses] Add Expense Details Failure',
    AddExpenseLine = '[expenses] Add Expense Lines',
    AddExpenseLineSuccess = '[expenses] Add Expense Lines Success',
    AddExpenseLineFailure = '[expenses] Add Expense Lines Failure',
    AddExpenseReceipts = '[expenses] Add Expense Receipts',
    AddExpenseReceiptsSuccess = '[expenses] Add Expense Receipts Success',
    AddExpenseReceiptsFailure = '[expenses] Add Expense Receipts Failure',
    AddExpenseAttachment = '[expenses] Add Expense Attachment',
    AddExpenseAttachmentSuccess = '[expenses] Add Expense Attachment Success',
    AddExpenseAttachmentFailure = '[expenses] Add Expense Attachment Failure',
    LoadExpenseSummaries = '[expenses] Load Expense Summaries',
    SetExpenseSummaries = '[expenses] Set Expense Summaries',
    LoadExpenseSummary = '[expenses] Load Expense Summary',
    SelectDirectReport = '[expenses] Select Direct Report',
    EditExpenseSummary = '[expenses] Edit Expense Summary',
    EditExpenseDetail = '[expenses] Edit Expense Detail',
    AddDraftExpenseLine = '[expenses] Add Draft Expense Line',
    SetPostSaveDetailNavigation = '[expenses] Set Post Save Detail Navigation',
    EditExpenseReceipt = '[expenses] Edit Expense Receipt',
    LoadExpenseReceiptAttachment = '[expenses] Load Expense Receipt Attachment',
    EditExpenseReceiptAttachment = '[expenses] Edit Expense Receipt Attachment',
    DeleteExpenseSummary = '[expenses] Delete Expense Summary',
    DeleteExpenseDetail = '[expenses] Delete Expense Detail',
    DeleteExpenseReceipt = '[expenses] Delete Expense Receipt',
    RemoveExpenseSummary = '[expenses] Remove Expense Summary',
    RemoveExpenseDetail = '[expenses] Remove Expense Detail',
    RemoveExpenseReceipt = '[expenses] Remove Expense Receipt',
    OversignExpenseSummary = '[expenses] Oversign Expense Summary',
    ConvertExpense = '[expenses] Convert Expense',
    ValidateExpense = '[expenses] Validate Expense',
    UnvalidateExpense = '[expenses] Unvalidate Expense',
    ApproveExpense = '[expenses] Approve Expense',
    UnapproveExpense = '[expenses] Unapprove Expense',
    DisapproveExpense = '[expenses] Disapprove Expense',
    UpdateExpenseSummary = '[expenses] Update Expense Summary',
    LoadExpenseLines = '[expenses] Load Expense Lines',
    SetExpenseLines = '[expenses] Set Expense Lines',
    LoadExpenseReceipts = '[expenses] Load Expense Receipts',
    SetExpenseReceipts = '[expenses] Set Expense Receipts',
    LoadPaymentMethods = '[expenses] Load Payment Methods',
    SetPaymentMethods = '[expenses] Set Payment Methods',
    LoadUserPaymentMethods = '[expenses] Load User Payment Methods',
    SetUserPaymentMethods = '[expenses] Set User Payment Methods',
    LoadTravelMode = '[expenses] Load Travel Mode',
    SetTravelMode = '[expenses] Set Travel Mode',
    LoadReceiptImage = '[expenses] Load Receipt Image',
    SetReceiptImage = '[expenses] Set Receipt Image',
    LoadReceiptImageFailure = '[expenses] Load Receipt Image Failure',
    LoadExpenseTasks = '[expenses] Load Expense Tasks',
    SetExpenseTasks = '[expenses] Set Expense Tasks',
    LoadTravelRates = '[expenses] Load Travel Rates',
    SetTravelRates = '[expenses] Set Travel Rates',
    SelectTravelRate = '[expenses] Select Travel Rate',
    LoadTripLegTypes = '[expenses] Load Trip Leg Types',
    SetTripLegTypes = '[expenses] Set Trip Leg Types',
    AddSelectedExpense = '[expenses] Add Selected Expense',
    RemoveSelectedExpense = '[expenses] Remove Selected Expense',
    ClearSelectedExpenses = '[expenses] Clear Selected Expenses',
    LoadExpenseReports = '[expenses] LoadExpenseReports',
    SetExpenseReports = '[expenses] SetExpenseReports',
    ClearExpenseNumber = '[expenses] ClearExpenseNumber',
    UpdateExpenseLinePDBO = '[expenses] Update Expense Line PDBO',
    LoadExpenseLinePDBO = '[expenses] Load Expense Line PDBO',
    SetExpenseLinePDBO = '[expenses] Set Expense Line PDBO'
}

export class Reset implements Action {
    readonly type = ActionTypes.Reset;
}
export class ResetSelectedExpense implements Action {
    readonly type = ActionTypes.ResetSelectedExpense;
}
export class ClearMessages implements Action {
    readonly type = ActionTypes.ClearMessages;
}
export class AddExpenseSummary implements Action {
    constructor(public payload: IExpenseSummary) {}
    readonly type = ActionTypes.AddExpenseSummary;
}
export class AddExpenseSummarySuccess implements Action {
    constructor(public payload: IExpenseSummaryResponse) {}
    readonly type = ActionTypes.AddExpenseSummarySuccess;
}
export class AddExpenseSummaryFailure implements Action {
    constructor(public payload: IExpenseSummaryResponse) {}
    readonly type = ActionTypes.AddExpenseSummaryFailure;
}
export class LoadReceiptImageFailure implements Action {
    constructor(public payload: string) {}
    readonly type = ActionTypes.LoadReceiptImageFailure;
}
export class AddExpenseDetails implements Action {
    constructor(
        public payload: {
            summary: IExpenseSummary;
            details: Array<IExpenseLine>;
        }
    ) {}
    readonly type = ActionTypes.AddExpenseDetails;
}
export class AddExpenseDetailsFailure implements Action {
    constructor(public payload: IExpenseDetailResponse) {}
    readonly type = ActionTypes.AddExpenseDetailsFailure;
}
export class AddExpenseLine implements Action {
    constructor(
        public payload: { summary: IExpenseSummaryResponse; line: IExpenseLine }
    ) {}
    readonly type = ActionTypes.AddExpenseLine;
}
export class AddExpenseLineSuccess implements Action {
    constructor(public payload: IExpenseLineResponse) {}
    readonly type = ActionTypes.AddExpenseLineSuccess;
}
export class AddExpenseLineFailure implements Action {
    constructor(public payload: IExpenseLineResponse) {}
    readonly type = ActionTypes.AddExpenseLineFailure;
}
export class AddExpenseReceipts implements Action {
    constructor(
        public detail: IExpenseLine,
        public payload: Array<IExpenseReceipt>
    ) {}
    readonly type = ActionTypes.AddExpenseReceipts;
}
export class AddExpenseReceiptsSuccess implements Action {
    constructor(public payload: IExpenseReceiptResponse) {}
    readonly type = ActionTypes.AddExpenseReceiptsSuccess;
}
export class AddExpenseReceiptsFailure implements Action {
    constructor(public payload: IExpenseReceiptResponse) {}
    readonly type = ActionTypes.AddExpenseReceiptsFailure;
}
export class AddExpenseAttachment implements Action {
    constructor(
        public expenseNumber: number,
        public uniqueId: string,
        public meta: any,
        public file: any
    ) {}
    readonly type = ActionTypes.AddExpenseAttachment;
}
export class AddExpenseAttachmentSuccess implements Action {
    constructor(public payload: IGenericResponse<any>) {}
    readonly type = ActionTypes.AddExpenseAttachmentSuccess;
}
export class AddExpenseAttachmentFailure implements Action {
    constructor(public payload: IGenericResponse<any>) {}
    readonly type = ActionTypes.AddExpenseAttachmentFailure;
}
export class LoadExpenseSummary implements Action {
    constructor(
        public payload: { expenseNbr: number; EmployeeListID: string }
    ) {}
    readonly type = ActionTypes.LoadExpenseSummary;
}
export class LoadExpenseSummaries implements Action {
    constructor(
        public payload?: {
            startDate?: Date;
            endDate?: Date;
            tasks?: Array<fromLookups.ITask>;
            expenseItems?: Array<fromLookups.IExpenseItem>;
            employeeListID?: string;
            currentEmployeeListID?: string;
            expenseType?: ExpenseRequestTypeEnum;
        }
    ) {}
    readonly type = ActionTypes.LoadExpenseSummaries;
}

export class SetExpenseSummaries implements Action {
    constructor(public payload: Array<IExpenseSummary>) {}
    readonly type = ActionTypes.SetExpenseSummaries;
}

export class SelectDirectReport implements Action {
    readonly type = ActionTypes.SelectDirectReport;
    constructor(public payload: fromLookups.IDirectReport) {}
}

export class DeleteExpenseSummary implements Action {
    readonly type = ActionTypes.DeleteExpenseSummary;
    constructor(public payload: IExpenseSummary) {}
}
export class DeleteExpenseDetail implements Action {
    readonly type = ActionTypes.DeleteExpenseDetail;
    constructor(public payload: IExpenseLine) {}
}
export class DeleteExpenseReceipt implements Action {
    readonly type = ActionTypes.DeleteExpenseReceipt;
    constructor(public payload: IExpenseReceipt) {}
}
export class RemoveExpenseSummary implements Action {
    readonly type = ActionTypes.RemoveExpenseSummary;
    constructor(public payload: IExpenseSummary) {}
}
export class RemoveExpenseDetail implements Action {
    readonly type = ActionTypes.RemoveExpenseDetail;
    constructor(public payload: IExpenseLine) {}
}
export class RemoveExpenseReceipt implements Action {
    readonly type = ActionTypes.RemoveExpenseReceipt;
    constructor(public payload: IDeleteReceiptResponse) {}
}
export class OversignExpenseSummary implements Action {
    readonly type = ActionTypes.OversignExpenseSummary;
    constructor(public payload: IExpenseSummary) {}
}
export class ConvertExpense implements Action {
    readonly type = ActionTypes.ConvertExpense;
    constructor(public payload: { summary: IExpenseSummary; roleId: number }) {}
}
export class ValidateExpense implements Action {
    readonly type = ActionTypes.ValidateExpense;
    constructor(
        public payload: { summaries: Array<IExpenseSummary>; roleId: number }
    ) {}
}
export class UnvalidateExpense implements Action {
    readonly type = ActionTypes.UnvalidateExpense;
    constructor(
        public payload: { summaries: Array<IExpenseSummary>; roleId: number }
    ) {}
}
export class ApproveExpense implements Action {
    readonly type = ActionTypes.ApproveExpense;
    constructor(
        public payload: { summaries: Array<IExpenseSummary>; roleId: number }
    ) {}
}
export class UnapproveExpense implements Action {
    readonly type = ActionTypes.UnapproveExpense;
    constructor(
        public payload: { summaries: Array<IExpenseSummary>; roleId: number }
    ) {}
}
export class DisapproveExpense implements Action {
    readonly type = ActionTypes.DisapproveExpense;
    constructor(
        public payload: { summaries: Array<IExpenseSummary>; roleId: number }
    ) {}
}
export class UpdateExpenseSummary implements Action {
    readonly type = ActionTypes.UpdateExpenseSummary;
    constructor(public payload: IExpenseSummary) {}
}

export class EditExpenseSummary implements Action {
    readonly type = ActionTypes.EditExpenseSummary;
    constructor(public payload: IExpenseSummary) {}
}

export class EditExpenseDetail implements Action {
    readonly type = ActionTypes.EditExpenseDetail;
    constructor(public payload: IExpenseLine) {}
}

/** Append an unsaved expense line to the report and select it for editing. */
export class AddDraftExpenseLine implements Action {
    readonly type = ActionTypes.AddDraftExpenseLine;
    constructor(public payload: Partial<IExpenseLine> = {}) {}
}

export interface PostSaveDetailNavigation {
    mode: 'new' | 'copy';
    copySource?: IExpenseLine;
}

/** After save completes, open a new blank line or a copy instead of re-selecting the saved line. */
export class SetPostSaveDetailNavigation implements Action {
    readonly type = ActionTypes.SetPostSaveDetailNavigation;
    constructor(public payload: PostSaveDetailNavigation | null) {}
}

export class EditExpenseReceipt implements Action {
    readonly type = ActionTypes.EditExpenseReceipt;
    constructor(public payload: IExpenseReceipt) {}
}
export class EditExpenseReceiptAttachment implements Action {
    readonly type = ActionTypes.EditExpenseReceiptAttachment;
    constructor(public payload: Partial<IAttachment>) {}
}

export class LoadExpenseLines implements Action {
    readonly type = ActionTypes.LoadExpenseLines;
    constructor(public payload: number) {}
}
export class SetExpenseLines implements Action {
    readonly type = ActionTypes.SetExpenseLines;
    constructor(public payload: Array<IExpenseLine>) {}
}
export class LoadExpenseReceipts implements Action {
    readonly type = ActionTypes.LoadExpenseReceipts;
    constructor(public payload: number) {}
}
export class SetExpenseReceipts implements Action {
    readonly type = ActionTypes.SetExpenseReceipts;
    constructor(public payload: Array<IExpenseReceipt>) {}
}
export class LoadExpenseReceiptAttachment implements Action {
    readonly type = ActionTypes.LoadExpenseReceiptAttachment;
    constructor(public payload: Partial<IExpenseReceipt>) {}
}
export class LoadPaymentMethods implements Action {
    readonly type = ActionTypes.LoadPaymentMethods;
    constructor(public payload: string) {}
}
export class SetPaymentMethods implements Action {
    readonly type = ActionTypes.SetPaymentMethods;
    constructor(public payload: Array<IPaymentMethod>) {}
}
export class LoadUserPaymentMethods implements Action {
    readonly type = ActionTypes.LoadUserPaymentMethods;
    constructor(public payload: string) {}
}
export class SetUserPaymentMethods implements Action {
    readonly type = ActionTypes.SetUserPaymentMethods;
    constructor(public payload: Array<UserPaymentMethod>) {}
}
export class LoadTravelMode implements Action {
    readonly type = ActionTypes.LoadTravelMode;
}
export class SetTravelMode implements Action {
    readonly type = ActionTypes.SetTravelMode;
    constructor(public payload: Array<ITravelMode>) {}
}
export class LoadReceiptImage implements Action {
    readonly type = ActionTypes.LoadReceiptImage;
    constructor(public payload: IExpenseReceipt) {}
}
export class SetReceiptImage implements Action {
    readonly type = ActionTypes.SetReceiptImage;
    constructor(public payload: string) {}
}
export class LoadExpenseTasks implements Action {
    readonly type = ActionTypes.LoadExpenseTasks;
    constructor(public payload: string) {}
}
export class SetExpenseTasks implements Action {
    readonly type = ActionTypes.SetExpenseTasks;
    constructor(public payload: Array<IExpenseTask>) {}
}
export class LoadTravelRates implements Action {
    readonly type = ActionTypes.LoadTravelRates;
    constructor(public payload: ITravelRate) {}
}
export class SetTravelRates implements Action {
    readonly type = ActionTypes.SetTravelRates;
    constructor(public payload: Array<ITravelRate>) {}
}
export class SelectTravelRate implements Action {
    readonly type = ActionTypes.SelectTravelRate;
    constructor(public payload: ITravelRate) {}
}
export class LoadTripLegTypes implements Action {
    readonly type = ActionTypes.LoadTripLegTypes;
}
export class SetTripLegTypes implements Action {
    readonly type = ActionTypes.SetTripLegTypes;
    constructor(public payload: Array<ITripLegType>) {}
}
export class AddSelectedExpense implements Action {
    readonly type = ActionTypes.AddSelectedExpense;
    constructor(public payload: number) {}
}
export class RemoveSelectedExpense implements Action {
    readonly type = ActionTypes.RemoveSelectedExpense;
    constructor(public payload: number) {}
}
export class ClearSelectedExpenses implements Action {
    readonly type = ActionTypes.ClearSelectedExpenses;
}
export class LoadExpenseReports implements Action {
    readonly type = ActionTypes.LoadExpenseReports;
    constructor(
        public roleId: number = 0,
        public activeOnly: boolean,
        public startDate: Date,
        public endDate: Date
    ) {}
}
export class SetExpenseReports implements Action {
    readonly type = ActionTypes.SetExpenseReports;
    constructor(public payload: Array<IDirectReport>) {}
}
export class ClearExpenseNumber implements Action {
    readonly type = ActionTypes.ClearExpenseNumber;
    constructor(public payload: number) {}
}

export class UpdateExpenseLinePDBO implements Action {
    readonly type = ActionTypes.UpdateExpenseLinePDBO;
    constructor(public pdbo: any) {}
}
export class LoadExpenseLinePDBO implements Action {
    readonly type = ActionTypes.LoadExpenseLinePDBO;
    constructor(public expenseLineId: number) {}
}
export class SetExpenseLinePDBO implements Action {
    readonly type = ActionTypes.SetExpenseLinePDBO;
    constructor(public pdbo: any) {}
}

export type ActionsUnion =
    | Reset
    | ResetSelectedExpense
    | ClearMessages
    | LoadExpenseSummaries
    | SetExpenseSummaries
    | LoadExpenseSummary
    | SelectDirectReport
    | EditExpenseSummary
    | EditExpenseDetail
    | AddDraftExpenseLine
    | SetPostSaveDetailNavigation
    | EditExpenseReceipt
    | DeleteExpenseSummary
    | DeleteExpenseDetail
    | RemoveExpenseSummary
    | RemoveExpenseDetail
    | RemoveExpenseReceipt
    | OversignExpenseSummary
    | ValidateExpense
    | ApproveExpense
    | UnvalidateExpense
    | UnapproveExpense
    | DisapproveExpense
    | UpdateExpenseSummary
    | LoadExpenseLines
    | SetExpenseLines
    | LoadExpenseReceipts
    | SetExpenseReceipts
    | LoadPaymentMethods
    | SetPaymentMethods
    | LoadUserPaymentMethods
    | SetUserPaymentMethods
    | LoadTravelMode
    | SetTravelMode
    | LoadReceiptImage
    | SetReceiptImage
    | LoadReceiptImageFailure
    | SetExpenseTasks
    | LoadExpenseTasks
    | LoadTravelRates
    | SetTravelRates
    | SelectTravelRate
    | LoadTripLegTypes
    | SetTripLegTypes
    | AddExpenseSummary
    | AddExpenseSummarySuccess
    | AddExpenseSummaryFailure
    | AddExpenseDetails
    | AddExpenseDetailsFailure
    | AddExpenseLine
    | AddExpenseLineSuccess
    | AddExpenseLineFailure
    | AddExpenseReceipts
    | AddExpenseReceiptsSuccess
    | AddExpenseReceiptsFailure
    | AddExpenseAttachment
    | AddExpenseAttachmentSuccess
    | AddExpenseAttachmentFailure
    | LoadExpenseReceiptAttachment
    | EditExpenseReceiptAttachment
    | AddSelectedExpense
    | RemoveSelectedExpense
    | ClearSelectedExpenses
    | LoadExpenseReports
    | SetExpenseReports
    | ClearExpenseNumber
    | UpdateExpenseLinePDBO
    | LoadExpenseLinePDBO
    | SetExpenseLinePDBO;
